import { Feature, FeatureCollection, Geometry } from "geojson";

import { ALL_COLORS, COLORS } from "@/constants/colors";
/**
 * Converts a DatasetResponse to a GeoJSON FeatureCollection.
 * Each row in the dataset becomes a feature, where the properties are mapped from the columns.
 * If attributesSchema is provided, it's used for mapping; otherwise, response.columns is used.
 */
import {
  CATCHMENT_GRID_ID_COLUMN_NAME,
  DATA_TYPE_VALUES,
  DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
  ORDINAL_TYPES,
  ORDINAL_TYPES_COLUMN,
  SPATIAL_AGGREGATION_NAME,
  SPATIAL_TYPE_TO_LAYER_TYPE
} from "@/constants/dataset";
import type { Timezone } from "@/constants/dateTime";
import { normalizeFilterValue } from "@/lib/utils";
import { defaultDataset } from "@/mocks/apis/dataset/data";
import { SpatialAggregationType } from "@/types/common";
import {
  AddUpdateRowsDatasetPayload,
  ATTRIBUTE_ID,
  CreateNewDatasetType,
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDataColumn,
  DatasetDataRowV2,
  DatasetDataRowValue,
  DatasetDataType,
  DatasetMetadata,
  DatasetMetadataStream,
  DatasetsMap,
  EColorLabel,
  EColorNames,
  H3Configuration,
  SchemaToDatasetMap,
  TransformedProperty
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { CatchmentDatasetConfig, EOutputAnalysisType } from "@/types/spatial";
import { isDatetime, translateToUserTimezone } from "@/utils/date";

import { area } from "@turf/turf";

import { processValue } from "./attributeProcessing";
import { handleCategoricalValues, handleNumericValues } from "./common";
import { convertFormatterDayjs } from "./date";
import { computeStepValue } from "./stepValue";
import { formatRenderValue } from "./string";
import { isWKTPoint, wktToGeoJSON } from "./wkt";

interface ProcessedRowData {
  properties: Record<string, string | number | boolean | null | undefined>;
  baseId: number | string | undefined;
  collectedGeometries: Array<{
    geometry: Geometry;
    sourceColumnName: string;
    columnIndex: number;
  }>;
}
interface ResolvedColumn {
  info: DatasetAttributeSchema | DatasetDataColumn | undefined;
  isVisible: boolean;
  isEnhancedGeo: boolean;
}

/**
 * Util function to check if the dataset data `rows` value is in v2 format.
 */

const processRowValue = (
  value: DatasetDataRowValue,
  columnInfo: DatasetAttributeSchema | DatasetDataColumn | undefined,
  useAttributesSchema: boolean,
  columnIndex: number,
  processedData: ProcessedRowData,
  spatialAggregationType?: SpatialAggregationType
) => {
  if (!columnInfo) {
    return;
  }
  const columnName = useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).attribute_name
    : (columnInfo as DatasetDataColumn).name;

  const isReferenceId = useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).is_reference_id ||
      columnName === "id"
    : columnName === "id";

  const isGeometryColumn = columnInfo.is_reference_geo;

  if (isReferenceId) {
    let idValue: string | number | undefined;

    if (typeof value === "string" || typeof value === "number") {
      idValue = value;
    } else if (value !== null && value !== undefined) {
      idValue = String(idValue);
    }

    processedData.baseId = idValue ?? columnIndex.toString();
    processedData.properties[columnName] = processedData.baseId;
  } else if (isGeometryColumn && typeof value === "string" && value) {
    // Check if spatialAggregationType is 'poi' and validate geometry is a point
    if (spatialAggregationType === "poi" && !isWKTPoint(value)) {
      // Skip non-point geometries when spatial aggregation type is 'poi'
      return;
    }
    if (
      spatialAggregationType === SPATIAL_AGGREGATION_NAME.H3 ||
      spatialAggregationType === SPATIAL_AGGREGATION_NAME.geohash.toLowerCase()
    ) {
      processedData.properties[columnName] = value;
      return;
    }
    // add geometry value to properties for Line and Polygon types, so it can be used in and feature info
    if (
      spatialAggregationType === SPATIAL_AGGREGATION_NAME.line.toLowerCase() ||
      spatialAggregationType === SPATIAL_AGGREGATION_NAME.polygon.toLowerCase()
    ) {
      processValue(value, columnInfo, processedData.properties);
    }

    const geometry = wktToGeoJSON(value)?.geometry;
    if (geometry) {
      processedData.collectedGeometries.push({
        geometry,
        sourceColumnName: columnName,
        columnIndex
      });
    }
  } else if (!isGeometryColumn) {
    processValue(
      value,
      columnInfo as DatasetDataColumn,
      processedData.properties
    );
  }
};

const createFeaturesFromRowData = (
  processedData: ProcessedRowData,
  rowIndex: number,
  features: Array<Feature<Geometry>>
) => {
  const { baseId, collectedGeometries, properties } = processedData;

  if (collectedGeometries.length > 0) {
    collectedGeometries.forEach(
      ({ geometry, sourceColumnName, columnIndex }, geometryIndex) => {
        const featureId =
          baseId !== undefined
            ? `${baseId}-${geometryIndex}`
            : `${rowIndex}-${columnIndex}-${geometryIndex}`;

        features.push({
          type: "Feature",
          id: featureId,
          geometry: geometry,
          properties: {
            ...properties,
            ["Geometry Source"]: sourceColumnName
          }
        });
      }
    );
  } else {
    const featureId = baseId !== undefined ? baseId : rowIndex.toString();
    features.push({
      type: "Feature",
      id: featureId,
      geometry: null as unknown as Geometry,
      properties: properties
    });
  }
};

/**
 * Builds two lookup maps from an `attributesSchema` array for efficient
 * column-to-schema matching.
 *
 * Two maps are produced:
 * - `schemaByName`     – keyed by `attribute_name` (1-to-1, last-write wins
 *                        when names collide).
 * - `schemaBySchemaId` – keyed by `id`, storing an **array** of entries
 *                        because multiple admin-area attributes can share the
 *                        same `id` (e.g. Village Name / Village Code both
 *                        reference `id: "ae95ab68-…"`).
 *
 * @param attributesSchema - The dataset schema entries returned by the API.
 * @returns An object containing both lookup maps.
 */
const buildSchemaLookupMaps = (
  attributesSchema: DatasetAttributeSchema[]
): {
  schemaByName: Map<string, DatasetAttributeSchema>;
  schemaBySchemaId: Map<string, DatasetAttributeSchema[]>;
} => {
  const schemaByName = new Map<string, DatasetAttributeSchema>();
  const schemaBySchemaId = new Map<string, DatasetAttributeSchema[]>();

  for (const attr of attributesSchema) {
    if (attr.attribute_name) {
      schemaByName.set(attr.attribute_name, attr);
    }
    if (attr.id) {
      const existing = schemaBySchemaId.get(attr.id);
      if (existing) {
        existing.push(attr);
      } else {
        schemaBySchemaId.set(attr.id, [attr]);
      }
    }
  }

  return { schemaByName, schemaBySchemaId };
};

/**
 * Attempts to find a matching `DatasetAttributeSchema` for a column by
 * comparing `col.schema_id` against the schema `id` lookup map.
 *
 * This is the **Priority 2 fallback** used when name-based matching fails
 * (e.g. a column was renamed in the UI but still carries the original
 * `schema_id`).
 *
 * When multiple schema entries share the same `id` (duplicate `schema_id`),
 * the first entry whose `position` has **not yet been claimed** is returned.
 * This prevents two columns from incorrectly resolving to the same schema row.
 *
 * @param col             - The dataset column to find a match for.
 * @param schemaBySchemaId - Lookup map of schema `id` → schema entries (may contain duplicates).
 * @param usedPositions   - Set of schema `position` values already claimed by earlier columns.
 * @returns The matched schema entry, or `undefined` if no match is found.
 */
const findBySchemaId = (
  col: DatasetDataColumn,
  schemaBySchemaId: Map<string, DatasetAttributeSchema[]>,
  usedPositions: Set<number>
): DatasetAttributeSchema | undefined => {
  if (!col.schema_id) return undefined;
  const candidates = schemaBySchemaId.get(col.schema_id);
  return candidates?.find((c) => !usedPositions.has(c.position));
};

/**
 * Creates a mapping from each **column index** (matching the row data array)
 * to its corresponding `DatasetAttributeSchema` entry.
 *
 * Matching strategy (per column, in priority order):
 * 1. **Name match** – `col.name === schema.attribute_name` (most reliable).
 * 2. **Schema-ID match** – `col.schema_id === schema.id` (handles renamed columns).
 *
 * Columns that exist in `data.rows` but have **no corresponding schema entry**
 * (e.g. `orbis_li_id`) are simply omitted from the map, so they will be
 * skipped during row processing without shifting the indices of other columns.
 *
 * A `usedSchemaPositions` set prevents the same schema entry from being
 * claimed by more than one column when `schema_id` values are duplicated
 * across admin-area attributes.
 *
 * @param columns          - The `columns` array from the dataset response.
 * @param attributesSchema - The full schema array from the API.
 * @returns A map of `columnIndex → DatasetAttributeSchema`.
 */
const matchColumnsToSchema = (
  columns: DatasetDataColumn[],
  attributesSchema: DatasetAttributeSchema[]
): Map<number, DatasetAttributeSchema> => {
  const schemaByColumnIndex = new Map<number, DatasetAttributeSchema>();
  const { schemaByName, schemaBySchemaId } =
    buildSchemaLookupMaps(attributesSchema);
  const usedSchemaPositions = new Set<number>();

  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const matched =
      schemaByName.get(col.name) ??
      findBySchemaId(col, schemaBySchemaId, usedSchemaPositions);

    if (matched) {
      usedSchemaPositions.add(matched.position);
      schemaByColumnIndex.set(i, matched);
    }
  }

  return schemaByColumnIndex;
};

/**
 * Resolves the metadata for a single column when `attributesSchema` is being used.
 *
 * - If the schema entry is missing or has `is_visible: false`, the column is
 *   marked as not visible and will be skipped during row processing.
 * - `isEnhancedGeo` is pre-computed here (once per column, not per row) to
 *   avoid redundant checks inside the hot row loop. It is `true` when either
 *   `schema.is_reference_geo` **or** `col.is_reference_geo` is set.
 *
 * Note: we intentionally do NOT fall back to `col.data_type === TYPE_GEOMETRY`
 * because some datasets contain geometry columns that are deliberately excluded
 * from the map (e.g. secondary/unused geometry columns where both schema and
 * column have `is_reference_geo: false`). Checking `data_type` would force
 * those unwanted geometries onto the map.
 *
 * @param col    - The column definition from the dataset response.
 * @param schema - The matched schema entry (may be `undefined` if no match was found).
 * @returns A `ResolvedColumn` with pre-computed visibility and geometry flags.
 */
const resolveColumnWithSchema = (
  col: DatasetDataColumn,
  schema: DatasetAttributeSchema | undefined
): ResolvedColumn => {
  if (!schema?.is_visible) {
    return { info: undefined, isVisible: false, isEnhancedGeo: false };
  }

  // Only trust the explicit is_reference_geo flag from either source.
  // Do NOT fall back to data_type check — it would incorrectly promote
  // unused geometry columns (where is_reference_geo is intentionally false).
  const isEnhancedGeo = schema.is_reference_geo || col?.is_reference_geo;

  return { info: schema, isVisible: true, isEnhancedGeo };
};

/**
 * Pre-computes an array of `ResolvedColumn` metadata — one entry per column index.
 *
 * This runs **once before the row loop**, so that the inner per-cell loop only
 * needs a cheap array access and boolean check instead of repeated `Map.get()`
 * lookups, visibility checks, and geometry-type comparisons on every cell.
 *
 * When `useAttributesSchema` is `false`, each column is resolved directly from
 * the `columns` array with no schema involvement.
 *
 * @param columns              - The `columns` array from the dataset response.
 * @param schemaByColumnIndex  - The column-index-to-schema map built by `matchColumnsToSchema`.
 * @param useAttributesSchema  - Whether an `attributesSchema` was provided.
 * @returns An array aligned with the column indices, each containing pre-resolved metadata.
 */
const preComputeColumnMetadata = (
  columns: DatasetDataColumn[],
  schemaByColumnIndex: Map<number, DatasetAttributeSchema>,
  useAttributesSchema: boolean
): ResolvedColumn[] => {
  return columns.map((col, i) => {
    if (!useAttributesSchema) {
      return {
        info: col,
        isVisible: !!col?.is_visible,
        isEnhancedGeo: col.is_reference_geo
      };
    }
    return resolveColumnWithSchema(col, schemaByColumnIndex.get(i));
  });
};

/**
 * Processes a single data row, delegating each cell to `processRowValue` and
 * then converting the accumulated result into GeoJSON Feature(s) via
 * `createFeaturesFromRowData`.
 *
 * For each cell in the row:
 * - Non-visible or unmatched columns are skipped via the pre-computed
 *   `resolvedColumns` metadata (no runtime type checks needed).
 * - Geometry columns that were flagged as `isEnhancedGeo` during pre-computation
 *   receive a shallow-copied `columnInfo` with `is_reference_geo: true`, ensuring
 *   `processRowValue` correctly identifies them as geometry. The spread only
 *   happens for geometry columns (typically 1–2 per row), not for every cell.
 *
 * @param rowData               - The raw row values array.
 * @param rowIndex              - The index of this row in the dataset (used for feature ID fallback).
 * @param resolvedColumns       - Pre-computed column metadata array.
 * @param useAttributesSchema   - Whether an `attributesSchema` was provided.
 * @param spatialAggregationType - The dataset's spatial type (e.g. "poi", "adm_area", "h3").
 * @param features              - The output array; new Feature(s) are pushed directly into it.
 */
const processRow = (
  rowData: DatasetDataRowV2,
  rowIndex: number,
  resolvedColumns: ResolvedColumn[],
  useAttributesSchema: boolean,
  spatialAggregationType: SpatialAggregationType,
  features: Array<Feature<Geometry>>
): void => {
  const processedData: ProcessedRowData = {
    properties: {},
    baseId: undefined,
    collectedGeometries: []
  };

  for (let columnIndex = 0; columnIndex < rowData.length; columnIndex++) {
    const resolved = resolvedColumns[columnIndex];
    if (!resolved?.isVisible || !resolved.info) continue;

    const columnInfo =
      useAttributesSchema && resolved.isEnhancedGeo
        ? { ...resolved.info, is_reference_geo: true }
        : resolved.info;

    processRowValue(
      rowData[columnIndex],
      columnInfo,
      useAttributesSchema,
      columnIndex,
      processedData,
      spatialAggregationType
    );
  }

  createFeaturesFromRowData(processedData, rowIndex, features);
};

/**
 * Converts a `DatasetData` response into a GeoJSON `FeatureCollection`.
 *
 * Each row in the dataset becomes one or more GeoJSON Features, where
 * properties are mapped from columns and geometry is parsed from WKT strings.
 *
 * **Schema matching strategy** (when `attributesSchema` is provided):
 *
 * The `columns` array (from the API response) and `attributesSchema` may not
 * be aligned by index — for example, columns can contain internal fields like
 * `orbis_li_id` that have no corresponding schema entry, causing all subsequent
 * indices to shift. To solve this, columns are matched to schema entries by:
 *
 * 1. **Name** – `col.name === schema.attribute_name` (primary, most reliable).
 * 2. **Schema ID** – `col.schema_id === schema.id` (fallback for renamed columns).
 *
 * **Geometry detection**:
 *
 * A column is treated as geometry only when `is_reference_geo` is `true` in
 * either the matched schema entry or the column definition. The `data_type`
 * field is intentionally **not** used as a fallback because some datasets
 * contain secondary geometry columns that are deliberately excluded from
 * the map (`is_reference_geo: false` in both sources).
 *
 * **Performance optimizations**:
 *
 * - Column metadata (visibility, geometry flag) is pre-computed **once** before
 *   the row loop, avoiding repeated Map lookups and type checks per cell.
 * - Object spread for enhanced geometry info only occurs on actual geometry
 *   columns (typically 1–2), not on every cell in every row.
 *
 * @param datasetData       - The dataset response containing columns, rows, and spatial type.
 * @param attributesSchema  - Optional schema array for richer column metadata.
 *                            When omitted, `datasetData.columns` is used directly.
 * @returns A GeoJSON `FeatureCollection` with one Feature per row (or per geometry when
 *          a row contains multiple geometry columns).
 *
 * @example
 * ```typescript
 * // Without schema — uses columns directly
 * const fc = convertDatasetResponseToFeatureCollection(datasetData);
 *
 * // With schema — uses name/schema_id matching + enhanced geo detection
 * const fc = convertDatasetResponseToFeatureCollection(datasetData, schema);
 * ```
 */
export const convertDatasetResponseToFeatureCollection = (
  datasetData: DatasetData,
  attributesSchema?: DatasetAttributeSchema[]
): FeatureCollection<Geometry> => {
  if (!datasetData) return { type: "FeatureCollection", features: [] };

  const { columns, rows, spatial_aggregation_type } = datasetData;
  const useAttributesSchema = !!attributesSchema;
  const features: Array<Feature<Geometry>> = [];

  const schemaByColumnIndex =
    useAttributesSchema && attributesSchema
      ? matchColumnsToSchema(columns, attributesSchema)
      : new Map<number, DatasetAttributeSchema>();

  const resolvedColumns = preComputeColumnMetadata(
    columns,
    schemaByColumnIndex,
    useAttributesSchema
  );

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    processRow(
      rows[rowIndex],
      rowIndex,
      resolvedColumns,
      useAttributesSchema,
      spatial_aggregation_type,
      features
    );
  }

  return { type: "FeatureCollection", features };
};

/**
 * Gets all unique property keys from feature properties
 */
export const getCategoriesFromDatasetReponse = (
  response: DatasetData
): {
  type: DatasetDataType;
  value: string;
}[] => {
  const categories: {
    type: DatasetDataType;
    value: string;
  }[] = [];

  response.columns.forEach((column) => {
    if (
      column.name === "geom" ||
      column.name === "id" ||
      CATCHMENT_GRID_ID_COLUMN_NAME.includes(column.name)
    )
      return;

    categories.push({
      type: column.data_type,
      value: column.name
    });
  });

  return categories;
};

/**
 * Calculates color distribution ranges for feature properties based on their type.
 * For string values, it creates color categories.
 * For numeric values, it creates quantile ranges.
 */
export const calculateFeaturePropertyColorRanges = (
  featureCollection: FeatureCollection<Geometry>,
  propertyName: string,
  isOrdinal: boolean,
  step: number
) => {
  // Extract property values
  const propertyValues = featureCollection.features.map(
    (f) => f.properties?.[propertyName]
  );

  return isOrdinal
    ? handleCategoricalValues(propertyValues, step)
    : handleNumericValues(propertyValues, step);
};

/*
 * Transforms properties of a selected feature into a more structured format
 * by associating them with their respective categories.
 * This is useful for displaying properties in a categorized manner.
 */

export const transformPropertiesWithCategory = (
  properties: SelectedFeature,
  categories: DatasetAttributeSchema[],
  timezone?: Timezone
): TransformedProperty[] => {
  return Object.entries(properties).reduce<TransformedProperty[]>(
    (acc, [key, value]) => {
      const category = categories.find((cat) => cat.attribute_name === key);
      if (category && isDatetime(category.data_type)) {
        const datasetDataType = SchemaToDatasetMap[category.data_type];
        const displayValue = translateToUserTimezone(
          timezone,
          datasetDataType,
          value
        );

        acc.push({
          key,
          value: displayValue,
          data_type: category.data_type,
          data_label: category.data_label
        });
      }
      return acc;
    },
    []
  );
};

/**
 * Transforms an array of properties into a key-value pair object.
 * Each property is formatted based on its data type using the `formatRenderValue` function.
 * This transformation is useful for efficiently accessing attributes by their keys
 * and displaying them in a structured format.
 */
export const transformVisibleAttributes = (
  transformProperty: TransformedProperty[],
  timezone?: Timezone
): Record<string, string> => {
  return transformProperty.reduce<Record<string, string>>((acc, item) => {
    const safeValue = item.value ?? "";
    const formattedValue = formatRenderValue({
      type: item.data_type,
      value: safeValue,
      timezone: timezone
    });
    acc[item.key] = String(formattedValue);
    return acc;
  }, {});
};

/** Function to return only the available/not deleted `datasets` from the given `datasets` slice */
export const getAvailableDatasets = (datasets: DatasetsMap): DatasetsMap => {
  return Object.fromEntries(
    Object.entries(datasets).filter(([, dataset]) => !!dataset.data.available)
  );
};

/**
 * Defines matching rules for selecting a default attribute based on spatial aggregation type.
 *
 * Each rule specifies:
 * - @param spatialType: The spatial aggregation type this rule applies to (e.g. H3, POI, polygon)
 * - @param dataType: The expected data type of the matching attribute
 * - @param isMatchingAttribute: A predicate function to further validate the attribute by name/column
 *
 * Rules are evaluated in order and used by `getDefaultSelectedCategory` as priority step 2,
 * after checking for a "score" attribute.
 *
 * Current rules:
 * - H3       → First INT attribute whose name includes "count"
 * - POI      → First TEXT attribute where both name and column equal "name"
 * - Polygon  → First TEXT attribute where both name and column equal "class"
 */
const SPATIAL_AGGREGATION_ATTRIBUTE_BY_TYPE: Array<{
  spatialType: string;
  dataType: DataSchemaType;
  isMatchingAttribute: (attr: DatasetAttributeSchema) => boolean;
}> = [
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.H3,
    dataType: DataSchemaType.INT,
    isMatchingAttribute: (attr) =>
      !!attr.attribute_name?.toLowerCase().includes("count")
  },
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.poi.toLowerCase(),
    dataType: DataSchemaType.TEXT,
    isMatchingAttribute: (attr) =>
      attr.attribute_name?.toLowerCase() === "name" &&
      attr.attribute_column?.toLowerCase() === "name"
  },
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.polygon.toLowerCase(),
    dataType: DataSchemaType.TEXT,
    isMatchingAttribute: (attr) =>
      attr.attribute_name?.toLowerCase() === "class" &&
      attr.attribute_column?.toLowerCase() === "class"
  }
];

/**
 * Finds a suitable attribute from the dataset's attributes based on the spatial aggregation type.
 * This function is used as part of the logic to determine the default selected category for a dataset.
 * It checks the dataset's attributes against predefined rules for specific spatial types (e.g., H3, POI, Polygon) to find an attribute that matches the expected data type and naming conventions.
 * @param attributes - The array of dataset attribute schemas to search through.
 * @param spatialAggregationType - The spatial aggregation type of the dataset, which influences the selection criteria for the attribute.
 * @returns The matching attribute schema if found, otherwise undefined.
 **/
const findSpatialAggregationTypeAttribute = (
  attributes: DatasetAttributeSchema[],
  spatialAggregationType?: SpatialAggregationType
): DatasetAttributeSchema | undefined => {
  const matchedRule = SPATIAL_AGGREGATION_ATTRIBUTE_BY_TYPE.find(
    ({ spatialType }) => spatialType === spatialAggregationType
  );
  return matchedRule
    ? attributes.find(
        (attr) =>
          attr.data_type === matchedRule.dataType &&
          matchedRule.isMatchingAttribute(attr)
      )
    : undefined;
};

/**
 * Selects the most appropriate attribute to use as the default category for dataset visualization.
 *
 * The selection follows a priority order:
 * 1. Prefers the "score" attribute if it exists (case-insensitive)
 * 2. Otherwise, selects the first non-special, non-geometry attribute
 * 3. Falls back to the first attribute in the array
 * 4. Returns undefined if the array is empty
 *
 * @param attributes - Array of dataset attribute schemas to choose from
 * @param spatialAggregationType - The spatial aggregation type of the dataset, which may influence attribute selection for certain types (e.g. H3, POI)
 * @returns The selected attribute schema that should be used as the default category, or undefined if no attributes exist
 *
 * @example
 * ```typescript
 * const attributes = [
 *   { attribute_name: 'id', data_type: DataSchemaType.STRING },
 *   { attribute_name: 'score', data_type: DataSchemaType.NUMERIC },
 *   { attribute_name: 'geometry', data_type: DataSchemaType.GEOMETRY }
 * ];
 *
 * const defaultCategory = getDefaultSelectedCategory(attributes, SPATIAL_AGGREGATION_NAME.H3);
 * // Returns the 'score' attribute
 * ```
 */
export const getDefaultSelectedCategory = (
  attributes: DatasetAttributeSchema[],
  spatialAggregationType?: SpatialAggregationType
): DatasetAttributeSchema | undefined => {
  if (attributes.length === 0) return undefined;

  // 1. Prefer the "score" attribute if it exists
  const scoreAttribute = attributes.find(
    (attr) => attr.attribute_name?.toLowerCase() === "score"
  );
  if (scoreAttribute) return scoreAttribute;

  // 2. Match by spatial type rules (H3 → count, POI → name, polygon → class)
  const spatialAggregationTypeAttribute = findSpatialAggregationTypeAttribute(
    attributes,
    spatialAggregationType
  );
  if (spatialAggregationTypeAttribute) return spatialAggregationTypeAttribute;

  // 3. Otherwise, prefer the first non-special, non-geometry attribute
  const regularAttribute = attributes.find(
    (attr) =>
      !(attr.attribute_name in ATTRIBUTE_ID) &&
      attr.data_type !== DataSchemaType.GEOMETRY
  );
  if (regularAttribute) return regularAttribute;

  // 4. Fallback: first attribute
  return attributes[0];
};

/**
 * Spatial types that should be visible by default for analysis results.
 */
const VISIBLE_SPATIAL_TYPES = [
  EOutputAnalysisType.H3,
  EOutputAnalysisType.GEOHASH,
  EOutputAnalysisType.GRID,
  EOutputAnalysisType.PROFILING
] as const;

/**
 * Checks if a spatial aggregation type should be visible for analysis results.
 */
const isVisibleSpatialType = (spatialType: string | undefined): boolean => {
  return VISIBLE_SPATIAL_TYPES.includes(spatialType as EOutputAnalysisType);
};

/**
 * Determines visibility based on whether it's an analysis result and the spatial type.
 */
const getDatasetVisibility = (
  isAnalysisResult: boolean,
  spatialType: string | undefined
): { isVisible: boolean; visibility: "visible" | "none" } => {
  const shouldBeVisible = isAnalysisResult && isVisibleSpatialType(spatialType);
  return {
    isVisible: shouldBeVisible,
    visibility: shouldBeVisible ? "visible" : "none"
  };
};

/**
 * Creates the default dataset style configuration.
 */
const createDefaultDatasetStyle = (
  defaultCategory: DatasetAttributeSchema | undefined,
  visibility: "visible" | "none"
): Dataset["style"] => ({
  ...defaultDataset.style,
  visibility,
  labelConfig: {
    ...defaultDataset.style.labelConfig,
    attribute_id: defaultCategory?.id ?? "",
    attribute_name: defaultCategory?.attribute_name ?? ""
  },
  stroke: {
    ...defaultDataset.style.stroke,
    attribute_id: defaultCategory?.id ?? "",
    attribute_name: defaultCategory?.attribute_name ?? ""
  }
});

/**
 * Creates a default spatial dataset object with configured properties, styles, and visibility settings.
 *
 * This function constructs a Dataset object from raw dataset data and attributes, applying
 * default configurations and determining visibility based on whether it's an analysis result
 * and the spatial aggregation type.
 *
 * @param {Object} params - The parameters object
 * @param {DatasetData} params.data - The raw dataset data containing id, name, spatial type, and rows
 * @param {DatasetAttributeSchema[]} params.attributes - Array of dataset attribute schemas
 * @param {boolean} [params.isAnalysisResult=false] - Whether this dataset is from an analysis result
 *
 * @returns {Dataset} A configured Dataset object with:
 *   - data: Basic dataset information (id, name, spatial_aggregation_type, total_rows)
 *   - properties: Visibility and fetching state based on analysis result and spatial type
 *   - style: Visibility styling ("visible" or "none") based on spatial aggregation type
 *   - categories: The provided attributes array
 *   - selectedCategory: Default selected category from attributes
 *
 * @example
 * ```typescript
 * const dataset = createDefaultSpatialDataset({
 *   data: {
 *     dataset_id: "123",
 *     name: "Population Data",
 *     spatial_aggregation_type: EOutputAnalysisType.GRID,
 *     rows: [...]
 *   },
 *   attributes: [...],
 *   isAnalysisResult: true
 * });
 * ```
 */
export const createDefaultSpatialDataset = ({
  data,
  attributes,
  isAnalysisResult = false
}: {
  data: DatasetData;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
}): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributes,
    data.spatial_aggregation_type
  );
  const { isVisible, visibility } = getDatasetVisibility(
    isAnalysisResult,
    data.spatial_aggregation_type
  );

  return {
    data: {
      ...defaultDataset.data,
      id: data.dataset_id,
      name: data.name,
      display_name: data.display_name ?? data.name,
      spatial_aggregation_type: data.spatial_aggregation_type,
      total_rows: data.rows.length,
      short_description: "",
      dataset_size_kb: data.dataset_size_kb
    },
    properties: {
      isVisible,
      spatialType: data.spatial_aggregation_type,
      isFetching: !isAnalysisResult
    },
    style: createDefaultDatasetStyle(defaultCategory, visibility),
    categories: attributes,
    selectedCategory: defaultCategory ?? DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE
  };
};

export const createSpatialDatasetFromMetadata = ({
  data,
  attributes,
  isAnalysisResult = false,
  h3Configuration = null
}: {
  data: DatasetMetadataStream;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
  h3Configuration?: H3Configuration | null;
}): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributes,
    data.spatial_aggregation_type
  );
  const { isVisible, visibility } = getDatasetVisibility(
    isAnalysisResult,
    data.spatial_aggregation_type
  );

  return {
    data: {
      ...defaultDataset.data,
      id: data.dataset_id,
      name: data.name,
      display_name: data.name,
      spatial_aggregation_type: data.spatial_aggregation_type,
      total_rows: data.total_rows,
      short_description: "",
      dataset_size_kb: data.dataset_size_kb
    },
    properties: {
      isVisible,
      spatialType: data.spatial_aggregation_type,
      isFetching: false
    },
    style: createDefaultDatasetStyle(defaultCategory, visibility),
    categories: attributes,
    selectedCategory: defaultCategory ?? DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    h3Configuration
  };
};

/**
 * Checks if a dataset has a 'score' column and if all of its values are null.
 */
export const hasNoScore = (dataset: DatasetData): boolean => {
  const scoreColumnIndex = dataset.columns.findIndex(
    (column) => column.name.toLowerCase() === "score"
  );

  if (scoreColumnIndex === -1) return false;
  if (!dataset.rows?.length) return true;

  return dataset.rows.every((row) => {
    const value = row[scoreColumnIndex];

    if (value === null || value === undefined) return true;

    if (typeof value === "string") return value.trim().length === 0;

    if (typeof value === "object" && value !== null) {
      return Object.values(value).every((v) => {
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return v.trim().length === 0;
        return false;
      });
    }
    return false;
  });
};

/**
 * Util function to check if a given data type is one of the ordinal types.
 * @param dataType - The data type to check against the ordinal types.
 * @returns {boolean} - Returns true if the data type is one of the ordinal types, false otherwise.
 */
export const isOrdinalType = (
  dataType: DataSchemaType | DatasetDataType
): boolean => {
  return (
    ORDINAL_TYPES.includes(dataType) || ORDINAL_TYPES_COLUMN.includes(dataType)
  );
};

export const generateFilterableAttributes = <T extends DatasetDataColumn>(
  columns: T[] | undefined | null
) => {
  if (!columns || columns.length === 0) {
    return [];
  }

  return columns
    .filter((col) => col.is_visible)
    .map((col) => ({
      label: col.name,
      value: normalizeFilterValue(col.name),
      type: col.data_type,
      code: normalizeFilterValue(col.code)
    }));
};

export const formatAddAndUpdateDatasetRow = (
  data: Record<string, unknown>,
  metadata?: DatasetMetadata
): AddUpdateRowsDatasetPayload[] => {
  if (!metadata) return [];

  const allowedNames = new Set(metadata.schema.map((m) => m.code));

  const mappedData = Object.entries(data).map(([key, value]) => {
    const schema = metadata.schema.find((s) => s.code === key);
    // Handle date/time types
    if (
      schema?.data_type === DataSchemaType.DATE ||
      schema?.data_type === DataSchemaType.DATE_TIME ||
      schema?.data_type === DataSchemaType.TIME
    ) {
      return {
        code: key,
        name: key,
        value: convertFormatterDayjs(
          value as string,
          schema?.time_format || "",
          schema.data_type
        )
      };
    }

    // Handle geometry types
    if (schema?.data_type === DataSchemaType.GEOMETRY) {
      const format = data[`format_geom_${key}`];

      if (format === "coordinates") {
        const latitude = data[`latitude_geom_input_${key}`];
        const longitude = data[`longitude_geom_input_${key}`];
        return {
          code: key,
          name: key,
          value: `POINT(${longitude} ${latitude})`
        };
      }

      if (format === "wkt") {
        return {
          code: key,
          name: key,
          value: data[`wkt_geom_input_${key}`] as string
        };
      }

      if (format === "geojson") {
        return {
          code: key,
          name: key,
          value: data[`geojson_geom_input_${key}`] as string
        };
      }

      if (format === "draw_polygon") {
        return {
          code: key,
          name: key,
          value: data[`draw_polygon_${key}`] as string
        };
      }

      return {
        code: key,
        name: key,
        value: value as string
      };
    }

    // Default (stringify all other types)
    return {
      code: key,
      name: key,
      value: String(value)
    };
  });

  return mappedData.filter((item) => allowedNames.has(item.code));
};

/**
 * Creates dataset attributes from column data
 * @param columns - Array of column data from dataset
 * @returns Array of DatasetAttributeSchema objects
 */
export const createDatasetAttributes = (
  columns: DatasetData["columns"]
): DatasetAttributeSchema[] => {
  return columns.map((column, index) => ({
    ...DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    id: index.toString(),
    code: column.code,
    attribute_name: column.name,
    data_type: DATA_TYPE_VALUES[column.data_type],
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: index,
    is_visible: column?.is_visible ?? false,
    data_label: column?.data_label ?? undefined
  }));
};

export const createNewDataset = ({
  dataset,
  datasetId,
  datasetName,
  attributesSchema,
  h3Configuration = null,
  styleConfig,
  totalRows = 0,
  index,
  group_by,
  is_global_admin_area = false
}: CreateNewDatasetType): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributesSchema,
    dataset.spatial_aggregation_type
  );
  const colors =
    EColorNames[ALL_COLORS[index || 0] as keyof typeof EColorNames];
  const defaultColorSteps = computeStepValue(totalRows);
  return {
    data: {
      ...dataset,
      id: datasetId,
      name: dataset.name,
      display_name: datasetName,
      schema: dataset.schema,
      available: dataset.available ?? true
    },
    properties: {
      isVisible: dataset.available ?? true,
      spatialType: dataset.spatial_aggregation_type
    },
    style: {
      weight: 1,
      opacity: 0.7,
      color: "#fff",
      fillColor: COLORS[styleConfig?.colorFamily ?? colors][500],
      colorFamily: styleConfig?.colorFamily ?? colors,
      fillOpacity: styleConfig?.opacity ?? 0.7,
      visibility: "visible",
      colorRanges: [],
      labelConfig: {
        color_scale:
          styleConfig?.labelConfig?.color_scale ?? EColorLabel.BLACK_WHITE,
        attribute_id:
          styleConfig?.labelConfig?.attribute_id ?? defaultCategory?.id ?? "",
        attribute_name:
          attributesSchema.find(
            (item) => item.id === styleConfig?.labelConfig?.attribute_id
          )?.attribute_name ??
          defaultCategory?.attribute_name ??
          "",
        show_label: styleConfig?.labelConfig?.show_label ?? false,
        opacity: styleConfig?.labelConfig?.opacity ?? 0.7
      },
      rankConfig: {
        isEnabled: styleConfig?.rankConfig?.isEnabled ?? false,
        maxRanking: styleConfig?.rankConfig?.maxRanking ?? 10
      },
      stroke: {
        attribute_id:
          styleConfig?.strokeConfig?.attribute_id ?? defaultCategory?.id ?? "",
        attribute_name:
          styleConfig?.strokeConfig?.attribute_name ??
          defaultCategory?.attribute_name ??
          "",
        width: styleConfig?.strokeConfig?.width ?? 1,
        opacity: styleConfig?.strokeConfig?.opacity ?? 0.7
      },
      colorSteps: styleConfig?.colorSteps ?? defaultColorSteps
    },
    categories: attributesSchema,
    selectedCategory:
      styleConfig?.selectedCategory ??
      defaultCategory ??
      DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    h3Configuration,
    group_by,
    is_global_admin_area
  };
};

export function buildGridConfig(h3Configuration?: H3Configuration | null) {
  if (!h3Configuration) return {};

  return {
    grid_config: {
      level: h3Configuration.resolution
        ? Number(h3Configuration.resolution)
        : undefined,
      aggregation_type: h3Configuration.aggregationType
    }
  };
}

export function sumPolygonDatasetArea(
  datasets: CatchmentDatasetConfig["datasets"]
): number {
  let totalArea = 0;
  for (const entry of Object.values(datasets)) {
    if (entry.dataset?.data?.spatial_aggregation_type !== "polygon") continue;
    for (const feature of convertDatasetResponseToFeatureCollection(
      entry.rawDataset
    ).features) {
      if (
        feature.geometry?.type === "Polygon" ||
        feature.geometry?.type === "MultiPolygon"
      ) {
        totalArea += area(feature);
      }
    }
  }
  return totalArea;
}
